import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/stores/auth';
import { hashPassword } from '@mysecureroom/crypto';
import { bytesToBase64 } from '@mysecureroom/crypto';
import { api } from '../lib/api/client';
import { Button, Card, CardHeader, CardBody, Input } from '../lib/design-system/components';
import './Auth.css';

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Calculate password strength
  const calculatePasswordStrength = (pwd: string): { score: number; label: string; color: string } => {
    if (!pwd) return { score: 0, label: '', color: '' };

    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score += 1;
    if (/\d/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { score, label: 'Weak', color: 'var(--color-error-600)' };
    if (score <= 3) return { score, label: 'Medium', color: 'var(--color-secondary-600)' };
    return { score, label: 'Strong', color: 'var(--color-success-600)' };
  };

  const passwordStrength = !isLogin ? calculatePasswordStrength(password) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Generate deterministic salt from email for password hashing
      const encoder = new TextEncoder();
      const emailData = encoder.encode(email);
      const saltBuffer = await crypto.subtle.digest('SHA-256', emailData);
      const salt = new Uint8Array(saltBuffer).slice(0, 16);

      // Derive password hash (lower cost for auth)
      const passwordHashBytes = await hashPassword(password, salt);
      const passwordHash = bytesToBase64(passwordHashBytes);

      if (isLogin) {
        // Login
        const response = await login(email, passwordHash);

        // Check if email is verified
        if (response.user.emailVerified === false) {
          navigate('/verify-email');
        } else {
          navigate('/unlock');
        }
      } else {
        // Register
        const response = await api.post<{
          user: { id: string; email: string; name?: string };
          verificationCode?: string;
          message: string;
        }>('/auth/register', {
          email,
          name: name.trim() || undefined,
          password_hash: passwordHash,
        });

        // Show success message with verification code (dev only)
        const devCode = response.verificationCode
          ? ` Your code: ${response.verificationCode}`
          : '';
        setSuccess(`Account created successfully! Please log in to continue.${devCode}`);
        setIsLoading(false);

        // Clear form and switch to login after delay
        setTimeout(() => {
          setSuccess('');
          setName('');
          setPassword('');
          setIsLogin(true);
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-header">
          <img
            src="/msr-logo-primary.svg"
            alt="MySecureRoom"
            className="auth-logo"
          />
          <h1 className="heading-1">MySecureRoom</h1>
          <p className="text-large text-secondary">Privacy-first life operating system</p>
        </div>

        <Card elevation="lg" padding="lg">
          <CardHeader>
            <div className="auth-toggle">
              <button
                type="button"
                className={`toggle-btn ${isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(true)}
              >
                Login
              </button>
              <button
                type="button"
                className={`toggle-btn ${!isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(false)}
              >
                Register
              </button>
            </div>
          </CardHeader>

          <CardBody>
            <form onSubmit={handleSubmit} className="auth-form">
              {!isLogin && (
                <Input
                  label="Name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  autoComplete="name"
                  placeholder="Your name (optional)"
                />
              )}

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
                placeholder="your@email.com"
              />

              <div className="password-field">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>

              {!isLogin && passwordStrength && passwordStrength.score > 0 && (
                <div className="password-strength">
                  <div className="strength-label">
                    <span className="text-small text-secondary">Password Strength:</span>
                    <span className="text-small font-medium" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="strength-bar">
                    <div
                      className="strength-bar-fill"
                      style={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                </div>
              )}

              {success && (
                <div className="success-message">
                  <span className="text-small">✓ {success}</span>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="text-small">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isLoading}
              >
                {isLogin ? 'Login' : 'Create Account'}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
